import { useCallback, useEffect, useState } from "react";
import { useStudentData } from "@/context";

export const LESSON_STATUS_CHANGED_KEY = "LESSON_STATUS_CHANGED";

// ── Apply submission localStorage helpers (BLD2-549) ────────────────

const APPLY_SUBMITTED_PREFIX = "APPLY_SUBMITTED:";
const APPLY_SUBMITTED_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Write the submission key with a TTL timestamp. */
function markApplySubmitted(key: string): void {
  try {
    localStorage?.setItem?.(key, String(Date.now() + APPLY_SUBMITTED_TTL_MS));
  } catch { /* localStorage unavailable */ }
}

/** Check if a submission key exists and has not expired. Cleans up expired entries. */
function isApplyMarkedSubmitted(key: string): boolean {
  try {
    const value = localStorage?.getItem?.(key);
    if (!value) return false;
    const expiresAt = Number(value);
    if (Number.isNaN(expiresAt) || Date.now() > expiresAt) {
      localStorage?.removeItem?.(key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Listens for lesson status changes that happen in other browser tabs.
 *
 * When a lesson is started or completed in another tab, that tab writes
 * a LESSON_STATUS_CHANGED flag to localStorage. The native `storage`
 * event fires in all OTHER tabs (not the one that wrote it). This hook
 * picks that up and invalidates all cached grade data (terms, subjects,
 * week lessons) so the context refetches fresh data from the API.
 *
 * Previously this only invalidated terms data (via resetTermsDetails),
 * which left the subjects view and week view showing stale completion
 * status. See: BLD2-547.
 */
const useCrossTabLessonSync = () => {
  const { invalidateAllCachedData } = useStudentData();

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LESSON_STATUS_CHANGED_KEY || event.newValue === null) return;

      // Invalidate all cached grade data so every view refetches
      invalidateAllCachedData();
      setTimeout(() => localStorage?.removeItem?.(LESSON_STATUS_CHANGED_KEY), 0);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [invalidateAllCachedData]);
};

export default useCrossTabLessonSync;

// ── Apply submission cross-tab guard (BLD2-549) ────────────────────

interface UseApplySubmissionGuardParams {
  /** Unique lesson key (e.g. "110:4:1:english:3"). All parts are guaranteed
   *  present when the Apply section is rendered. */
  lessonKey: string;
}

interface UseApplySubmissionGuardReturn {
  /** True when another browser tab has already submitted this Apply activity. */
  isSubmittedElsewhere: boolean;
  /** Call after a successful submission to write the cross-tab guard key. */
  onSubmitSuccess: () => void;
}

/**
 * Cross-tab guard for the Apply section's digital activity submission.
 *
 * Prevents double-submission when the same lesson is open in multiple
 * browser tabs. Uses a localStorage key encoding the full lesson identity
 * with a 24-hour TTL.
 *
 * Behaviour:
 * - On mount: if the key exists, sets isSubmittedElsewhere = true
 *   (disables Submit button, shows info message).
 * - Storage event (real-time cross-tab): same — sets isSubmittedElsewhere.
 * - On successful submission: writes the localStorage key with TTL.
 *
 * The student can still see their in-progress work and navigate to
 * Reflection via the sidebar. When they navigate away from Apply and
 * back, useLessonSectionContent re-fetches and the backend returns a
 * review-mode Learnosity session showing the authoritative results.
 *
 * See: BLD2-549.
 */
export function useApplySubmissionGuard({
  lessonKey
}: UseApplySubmissionGuardParams): UseApplySubmissionGuardReturn {
  const [isSubmittedElsewhere, setIsSubmittedElsewhere] = useState(false);

  const storageKey = `${APPLY_SUBMITTED_PREFIX}${lessonKey}`;

  // On mount (or when the lesson changes): check if Apply was
  // already submitted in another tab.
  useEffect(() => {
    if (isApplyMarkedSubmitted(storageKey)) {
      setIsSubmittedElsewhere(true);
    }
  }, [storageKey]);

  // Listen for cross-tab Apply submissions while this tab is on the Apply page.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey || event.newValue === null) return;
      setIsSubmittedElsewhere(true);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [storageKey]);

  // Called by the view after a successful submission.
  const onSubmitSuccess = useCallback(() => {
    markApplySubmitted(storageKey);
  }, [storageKey]);

  return { isSubmittedElsewhere, onSubmitSuccess };
}
